import { DeleteOutlined } from '@ant-design/icons';
import { Col, DatePicker, Input, Radio, Row } from 'antd';
import moment, { Moment } from 'moment';
import { KeyboardEvent } from 'react';
import { LabeledField } from '../../../../../components/form-fields/labeled-field';
import { SquareButton } from '../../../../../components/action-buttons';
import type { PromotionForm } from '../../../../../stores/promotion';
import { PromotionAvailability } from '../../../../../stores/promotion';
import style from './style.module.scss';

const DAYS_OF_WEEK = [
    { label: 'Mo', value: 1 },
    { label: 'Tu', value: 2 },
    { label: 'We', value: 3 },
    { label: 'Th', value: 4 },
    { label: 'Fr', value: 5 },
    { label: 'Sa', value: 6 },
    { label: 'Su', value: 0 },
];

interface DayOfWeek {
    label: string;
    value: number;
}

function getDayName(dayValue: number): string {
    const day: DayOfWeek | undefined = DAYS_OF_WEEK.find(
        (d) => d.value === dayValue
    );
    if (!day) return 'Weekday';

    switch (day.label) {
        case 'Mo':
            return 'Mondays';
        case 'Tu':
            return 'Tuesdays';
        case 'We':
            return 'Wednesdays';
        case 'Th':
            return 'Thursdays';
        case 'Fr':
            return 'Fridays';
        case 'Sa':
            return 'Saturdays';
        case 'Su':
            return 'Sundays';
        default:
            return 'Weekdays';
    }
}

function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
        e.preventDefault();
    }
}

interface AvailabilityCardProps {
    availability: PromotionAvailability;
    values: PromotionForm;
    updateAvailability: (
        field: keyof PromotionAvailability,
        value: string | number | Date | null
    ) => void;
    removeAvailability: () => void;
}

export function AvailabilityCard({
    availability,
    values,
    updateAvailability,
    removeAvailability,
}: AvailabilityCardProps) {
    const disabledDate = (current: moment.Moment | null): boolean => {
        const mainStartDate = values.startDate
            ? moment(values.startDate).startOf('day')
            : null;
        const mainEndDate = values.endDate
            ? moment(values.endDate).endOf('day')
            : null;

        if (!current) return false;
        if (!mainStartDate || !mainEndDate) return false;

        return current.isBefore(mainStartDate) || current.isAfter(mainEndDate);
    };

    return (
        <div className={style.availability_card_container}>
            <div className={style.header}>
                <span className={style.header_title}>
                    Schedule for {getDayName(availability.dayOfWeek)}
                </span>
                <SquareButton
                    type="primary"
                    onClick={removeAvailability}
                    icon={<DeleteOutlined />}
                />
            </div>
            <div className={style.content}>
                <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                        <LabeledField label="Start date">
                            <DatePicker
                                format="YYYY-MM-DD"
                                placeholder="Select date"
                                value={
                                    availability.startDate
                                        ? moment(availability.startDate)
                                        : null
                                }
                                onChange={(date: Moment | null) => {
                                    const adjustedDate = date
                                        ? date.hour(0).minute(0).second(0)
                                        : null;
                                    updateAvailability(
                                        'startDate',
                                        adjustedDate
                                            ? adjustedDate.toDate()
                                            : null
                                    );
                                }}
                                disabledDate={disabledDate}
                                allowClear={false}
                                disabled={!values.startDate || !values.endDate}
                                className={style.full_width}
                            />
                        </LabeledField>
                    </Col>

                    <Col xs={24} md={12}>
                        <LabeledField label="End date">
                            <DatePicker
                                format="YYYY-MM-DD"
                                placeholder="Select date"
                                value={
                                    availability.endDate
                                        ? moment(availability.endDate)
                                        : null
                                }
                                onChange={(date: Moment | null) => {
                                    const adjustedDate = date
                                        ? date.hour(0).minute(0).second(0)
                                        : null;
                                    updateAvailability(
                                        'endDate',
                                        adjustedDate
                                            ? adjustedDate.toDate()
                                            : null
                                    );
                                }}
                                disabledDate={(current) => {
                                    // Apply main date range constraints
                                    if (disabledDate(current)) return true;

                                    if (!current || !availability.startDate)
                                        return false;
                                    return current.isBefore(
                                        moment(availability.startDate).startOf(
                                            'day'
                                        )
                                    );
                                }}
                                allowClear={true}
                                disabled={!values.startDate || !values.endDate}
                                className={style.full_width}
                            />
                        </LabeledField>
                    </Col>

                    <Col xs={24} md={24}>
                        <LabeledField
                            label="Time range"
                            helperText="Hours of the day when this promotion is available"
                        >
                            <div className={style.time_container}>
                                <Input
                                    value={availability.startHour}
                                    onChange={(e) =>
                                        updateAvailability(
                                            'startHour',
                                            parseInt(e.target.value, 10) || 0
                                        )
                                    }
                                    onKeyDown={handleKeyDown}
                                    className={style.time_input}
                                    addonAfter="h"
                                    min={0}
                                    max={24}
                                    type="number"
                                />
                                <span className={style.time_separator}>to</span>
                                <Input
                                    value={availability.endHour}
                                    onChange={(e) =>
                                        updateAvailability(
                                            'endHour',
                                            parseInt(e.target.value, 10) || 0
                                        )
                                    }
                                    onKeyDown={handleKeyDown}
                                    className={style.time_input}
                                    addonAfter="h"
                                    min={0}
                                    max={24}
                                    type="number"
                                />
                            </div>
                        </LabeledField>
                    </Col>

                    <Col xs={24} md={14}>
                        <LabeledField
                            label="Days available"
                            helperText="Select which day of the week this promotion is available"
                        >
                            <Radio.Group
                                value={availability.dayOfWeek}
                                onChange={(e) =>
                                    updateAvailability(
                                        'dayOfWeek',
                                        e.target.value
                                    )
                                }
                                className={style.days_container}
                            >
                                {DAYS_OF_WEEK.map((day) => (
                                    <Radio.Button
                                        key={day.value}
                                        value={day.value}
                                    >
                                        {day.label}
                                    </Radio.Button>
                                ))}
                            </Radio.Group>
                        </LabeledField>
                    </Col>

                    <Col xs={24} md={10}>
                        <LabeledField
                            label="Daily limit"
                            helperText="Maximum number of redemptions per day."
                        >
                            <Input
                                type="number"
                                min={0}
                                value={availability.dailyLimit || 0}
                                onChange={(e) =>
                                    updateAvailability(
                                        'dailyLimit',
                                        parseInt(e.target.value, 10)
                                    )
                                }
                                onKeyDown={handleKeyDown}
                                addonAfter="per day"
                                className={style.full_width}
                            />
                        </LabeledField>
                    </Col>
                </Row>
            </div>
        </div>
    );
}
